import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    FormControlLabel,
    Select,
    MenuItem,
    FormControl,
    FormHelperText,
    OutlinedInput,
    IconButton
} from '@material-ui/core';
import Clear from '@material-ui/icons/Clear';

import CardData from '../../../../../../utils/CardData';
import ColorPicker from '../../../../../../../../reusable/ColorPicker/ColorPicker';
import { useCustomStyles } from '../../../utils';
import { WIDGET_ANIMATION_TYPE_TRANSLATE_LABELS, WIDGET_ANIMATION_DIRECTIONS, WIDGET_ANIMATION_TYPES, WIDGET_ANIMATION_TYPE_HAS_DIRECTION, WIDGET_ANIMATION_TYPE_HAS_NO_DURATION } from '../../../../../../../../constants/scenarioConstant';
import arrow_down from '../../../../../../../../assets/images/arrow-90deg-down.svg'
import { WidgetProps } from '../../../../../widgetOverlay/widgetInteractionItem/WidgetProps';
import { EXIT_ANIMATION_HAS_ACTIVE_STATE, WIDGET_ANIMATION_DEFAULT_DURATIONS } from '../../../../../widgetOverlay/widgetInteractionItem/useWidgetAnimation';
import { getWidgetProps } from '../../../../../../utils/helper';

import NumberInput from './generalSettingsChild/NumberInput';
import { InteractivityProps } from './Interactivity';

import './Animation.scss';

interface AnimationProps extends InteractivityProps {
    showHoverSetting?: boolean;
    setHoverSetting: (_value: boolean) => void;
    handleBGColorChange: (_event: {} | undefined, _color: {}) => {};
    handleBGOpacityChange: (_event: {} | undefined, _color: {}) => {};
    handleHoverBGColorChange: (_event: {} | undefined, _color: {}) => {};
}

const defaultProps = {
    wordDuration: 500,
    characterDuration: 200,
}

const Animation: React.FC<AnimationProps> = ({ widgetTemplate: widgetTemplateProp, handleStylingAttributeChange }) => {
    const { t: translator } = useTranslation();
    const classes = useCustomStyles()

    const widgetTemplate = getWidgetProps(widgetTemplateProp) as WidgetProps.WidgetTemplate;
    const {
        type,
        style: {
            backgroundSettings: {
                activeBackgroundColor = '',
            } = {},
            transformSettings: {
                entryAnimation = '',
                entryAnimationDirection = '',
                entryAnimationDuration: entryAnimationDurationProp = entryAnimation === "charactersFade" ? defaultProps.characterDuration : entryAnimation === "wordsFade" || entryAnimation === "wordsSlide" ? defaultProps.wordDuration : (WIDGET_ANIMATION_DEFAULT_DURATIONS[entryAnimation] ?? 1000),
                entryAnimationDelay = 0,
                exitAnimation = '',
                exitAnimationDirection = '',
                exitAnimationDuration: exitAnimationDurationProp = exitAnimation === "charactersFade" ? defaultProps.characterDuration : exitAnimation === "wordsFade" || exitAnimation === "wordsSlide" ? defaultProps.wordDuration : (WIDGET_ANIMATION_DEFAULT_DURATIONS[exitAnimation] ?? 1000),
                exitAnimationDelay = 0,
                loopAnimation = '',
                loopAnimationDuration = 1000,
                loopAnimationDelay = 0,
            } = {
                entryAnimation: '',
                entryAnimationDirection: '',
                entryAnimationDuration: 1000,
                entryAnimationDelay: 0,
                exitAnimation: '',
                exitAnimationDirection: '',
                exitAnimationDuration: 1000,
                exitAnimationDelay: 0,
                loopAnimation: '',
                loopAnimationDuration: 1000,
                loopAnimationDelay: 0,
            },
        } = {},
    } = widgetTemplate;

    const entryAnimationDuration = entryAnimationDurationProp;
    const exitAnimationDuration = exitAnimationDurationProp;

    const entryAnimationTypes = WIDGET_ANIMATION_TYPES[type as keyof typeof WIDGET_ANIMATION_TYPES]?.entry || [];
    const loopAnimationTypes = WIDGET_ANIMATION_TYPES[type as keyof typeof WIDGET_ANIMATION_TYPES]?.loop || [];
    const exitAnimationTypes = WIDGET_ANIMATION_TYPES[type as keyof typeof WIDGET_ANIMATION_TYPES]?.exit || [];

    return (
		<CardData title={translator('Animation')}>
            <FormControlLabel
                className={'scenario-chart__control'}
                label={translator(
                    'SCENARIO_SCENE_EDITOR_WIDGET_SETTING_ANIMATION_IN'
                )}
                onChange={({ target }) => {
                    handleStylingAttributeChange(
                        `transformSettings`,
                        'entryAnimation',
                        (target as HTMLInputElement).value
                    );
                }}
                labelPlacement="start"
                control={
                    <Select displayEmpty variant="outlined" value={entryAnimation} className={classes.input}>
                        <MenuItem value={''} key={'none'}>
                            {translator('COMMON_NONE') as string}
                        </MenuItem>
                        {entryAnimationTypes.map((animationType) => (
                            <MenuItem value={animationType} key={animationType}>
                                {translator(WIDGET_ANIMATION_TYPE_TRANSLATE_LABELS.entry[animationType as keyof typeof WIDGET_ANIMATION_TYPE_TRANSLATE_LABELS.entry] || '') as string}
                            </MenuItem>
                        ))}
                    </Select>
                }
            />
            {WIDGET_ANIMATION_TYPE_HAS_DIRECTION.includes(entryAnimation) && <FormControlLabel
                className={'scenario-chart__control'}
                label={<img src={arrow_down} />}
                labelPlacement="start"
                control={
                    <FormControl>
                        <Select
                            displayEmpty
                            onChange={({ target }) => {
                                handleStylingAttributeChange(
                                    `transformSettings`,
                                    'entryAnimationDirection',
                                    (target as HTMLInputElement).value
                                );
                            }}
                            variant="outlined"
                            value={entryAnimationDirection}
                            className={classes.input}
                        >
                            <MenuItem value={""} key={'none'}>
                                {translator('COMMON_NONE') as string}
                            </MenuItem>
                            {WIDGET_ANIMATION_DIRECTIONS.map((direction) => (
                                <MenuItem value={direction.value} key={direction.value}>
                                    {translator(direction.label) as string}
                                </MenuItem>
                            ))}
                        </Select>
                        <FormHelperText>
                            {translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_ANIMATION_CHOOSE_ENTRY_DIRECTION') as string}
                        </FormHelperText>
                    </FormControl>
                }
            />}
            {entryAnimation && <FormControlLabel
                className={'scenario-chart__control'}
                label={<img src={arrow_down} />}
                labelPlacement="start"
                control={
                    <FormControl>
                        <div className="scene-widgets__sort-input-control">
                           {!WIDGET_ANIMATION_TYPE_HAS_NO_DURATION.includes(entryAnimation) && <NumberInput
                                id={`entry-animation-duration-input`}
                                label={translator('COMMON_DURATION') as string}
                                value={entryAnimationDuration as number}
                                min={0}
                                endAdornment="ms"
                                onChange={(value) => handleStylingAttributeChange(`transformSettings`, 'entryAnimationDuration', value)}
                            />}
                            <NumberInput
                                id={`entry-animation-delay-input`}
                                label={translator('COMMON_DELAY') as string}
                                value={entryAnimationDelay as number}
                                min={0}
                                endAdornment="ms"
                                onChange={(value) => handleStylingAttributeChange(`transformSettings`, 'entryAnimationDelay', value)}
                            />
                        </div>
                        <FormHelperText>
                            {translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_ANIMATION_CHOOSE_ENTRY_INFO') as string}
                        </FormHelperText>
                    </FormControl>
                }
            />}

            <FormControlLabel
                className={'scenario-chart__control'}
                label={translator(
                    'SCENARIO_SCENE_EDITOR_WIDGET_SETTING_ANIMATION_LOOP'
                )}
                onChange={({ target }) => {
                    handleStylingAttributeChange(
                        `transformSettings`,
                        'loopAnimation',
                        (target as HTMLInputElement).value
                    );
                }}
                labelPlacement="start"
                control={
                    <Select displayEmpty variant="outlined" value={loopAnimation} className={classes.input}>
                        <MenuItem value={''} key={'none'}>
                            {translator('COMMON_NONE') as string}
                        </MenuItem>
                        {loopAnimationTypes.map((animationType) => (
                            <MenuItem value={animationType} key={animationType}>
                                {translator(WIDGET_ANIMATION_TYPE_TRANSLATE_LABELS.loop[animationType as keyof typeof WIDGET_ANIMATION_TYPE_TRANSLATE_LABELS.loop] || '') as string}
                            </MenuItem>
                        ))}
                    </Select>
                }
            />
            {loopAnimation && <FormControlLabel
                className={'scenario-chart__control'}
                label={<img src={arrow_down} />}
                labelPlacement="start"
                control={
                    <FormControl>
                        <div className="scene-widgets__sort-input-control">
                            <NumberInput
                                label={translator('COMMON_DURATION') as string}
                                id={`loop-animation-duration-input`}
                                value={loopAnimationDuration as number}
                                min={0}
                                endAdornment="ms"
                                onChange={(value) => handleStylingAttributeChange(`transformSettings`, 'loopAnimationDuration', value)}
                            />
                            <NumberInput
                                label={translator('COMMON_DELAY') as string}
                                id={`loop-animation-delay-input`}
                                value={loopAnimationDelay as number}
                                min={0}
                                endAdornment="ms"
                                onChange={(value) => handleStylingAttributeChange(`transformSettings`, 'loopAnimationDelay', value)}
                            />
                        </div>
                        <FormHelperText>
                            {translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_ANIMATION_CHOOSE_LOOP_INFO') as string}
                        </FormHelperText>
                    </FormControl>
                }
            />}

            <FormControlLabel
                className={'scenario-chart__control'}
                label={translator(
                    'SCENARIO_SCENE_EDITOR_WIDGET_SETTING_ANIMATION_OUT'
                )}
                onChange={({ target }) => {
                    handleStylingAttributeChange(
                        `transformSettings`,
                        'exitAnimation',
                        (target as HTMLInputElement).value
                    );
                }}
                labelPlacement="start"
                control={
                    <Select displayEmpty variant="outlined" value={exitAnimation} className={classes.input}>
                        <MenuItem value={''} key={'none'}>
                            {translator('COMMON_NONE') as string}
                        </MenuItem>
                        {exitAnimationTypes.map((animationType) => (
                            <MenuItem value={animationType} key={animationType}>
                                {translator(WIDGET_ANIMATION_TYPE_TRANSLATE_LABELS.exit[animationType as keyof typeof WIDGET_ANIMATION_TYPE_TRANSLATE_LABELS.exit] || '') as string}
                            </MenuItem>
                        ))}
                    </Select>
                }
            />
            {WIDGET_ANIMATION_TYPE_HAS_DIRECTION.includes(exitAnimation) && <FormControlLabel
                className={'scenario-chart__control'}
                label={<img src={arrow_down} />}
                labelPlacement="start"
                control={
                    <FormControl>
                        <Select
                            displayEmpty
                            onChange={({ target }) => {
                                handleStylingAttributeChange(
                                    `transformSettings`,
                                    'exitAnimationDirection',
                                    (target as HTMLInputElement).value
                                );
                            }}
                            variant="outlined"
                            value={exitAnimationDirection}
                            className={classes.input}
                        >
                            <MenuItem value={""} key={'none'}>
                                {translator('COMMON_NONE') as string}
                            </MenuItem>
                            {WIDGET_ANIMATION_DIRECTIONS.map((direction) => (
                                <MenuItem value={direction.value} key={direction.value}>
                                    {translator(direction.label) as string}
                                </MenuItem>
                            ))}
                        </Select>
                        <FormHelperText>
                            {translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_ANIMATION_CHOOSE_EXIT_DIRECTION') as string}
                        </FormHelperText>
                    </FormControl>
                }
            />}
            {exitAnimation && <FormControlLabel
                className={'scenario-chart__control'}
                label={<img src={arrow_down} />}
                labelPlacement="start"
                control={
                    <FormControl>
                        <div className="scene-widgets__sort-input-control">
                           {!WIDGET_ANIMATION_TYPE_HAS_NO_DURATION.includes(exitAnimation) && <NumberInput
                                id={`exit-animation-duration-input`}
                                label={translator('COMMON_DURATION') as string}
                                value={exitAnimationDuration as number}
                                min={0}
                                endAdornment="ms"
                                onChange={(value) => handleStylingAttributeChange(`transformSettings`, 'exitAnimationDuration', value)}
                            />}
                            <NumberInput
                                id={`exit-animation-delay-input`}
                                label={translator('COMMON_DELAY') as string}
                                value={exitAnimationDelay as number}
                                min={0}
                                endAdornment="ms"
                                onChange={(value) => handleStylingAttributeChange(`transformSettings`, 'exitAnimationDelay', value)}
                            />
                        </div>
                        <FormHelperText>
                            {translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_ANIMATION_CHOOSE_EXIT_INFO') as string}
                        </FormHelperText>
                    </FormControl>
                }
            />}

            {EXIT_ANIMATION_HAS_ACTIVE_STATE.includes(exitAnimation) && (
                <FormControlLabel
                    className={'scenario-chart__control'}
                    label={translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_ANIMATION_ACTIVE_STATE')}
                    onChange={({ target }) => {
                        handleStylingAttributeChange(
                            `backgroundSettings`,
                            'activeBackgroundColor',
                            (target as HTMLInputElement).value
                        );
                    }}
                    labelPlacement="start"
                    control={
                        <FormControl>
                            <div className="scene-widgets__sort-input-control">
                                <OutlinedInput value={activeBackgroundColor} className={classes.input} />
                                <ColorPicker
                                    width="28px"
                                    height="28px"
                                    padding="4px"
                                    color={activeBackgroundColor}
                                    onChangeComplete={(color: any) =>
                                        handleStylingAttributeChange(`backgroundSettings`, 'activeBackgroundColor', color.hex)
                                    }
                                />
                                <IconButton
                                    size="small"
                                    onClick={() => handleStylingAttributeChange(`backgroundSettings`, 'activeBackgroundColor', '')}
                                >
                                    <Clear />
                                </IconButton>
                            </div>
                            <FormHelperText>
                                {translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_ANIMATION_ACTIVE_STATE_INFO') as string}
                            </FormHelperText>
                        </FormControl>
                    }
                />
            )}
        </CardData>
    )
}

export default Animation;